// Google Analytics 4 Event Tracking Utility

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

let GA_MEASUREMENT_ID: string | null = null;

/** Set from the GOOGLE_ANALYTICS_MEASUREMENT_ID secret (read server-side in the root loader). */
export const setMeasurementId = (id: string | null): void => { GA_MEASUREMENT_ID = id; };

export const OPEN_COOKIE_SETTINGS_EVENT = "cmo:open-cookie-settings";
export const openCookieSettings = (): void => {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
};

/** Withdraw consent: forget the acceptance, stop GA sending, and delete _ga / _ga_* cookies. */
export const revokeAnalyticsConsent = (): void => {
  try { window.localStorage.removeItem("cookie-consent"); } catch { /* ignore */ }
  if (GA_MEASUREMENT_ID) (window as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  const host = window.location.hostname;
  const bare = host.replace(/^www\./, "");
  const domains = ["", host, `.${host}`, bare, `.${bare}`];
  document.cookie.split(";").map((c) => (c.split("=")[0] ?? "").trim())
    .filter((name) => name === "_ga" || name.startsWith("_ga_"))
    .forEach((name) => domains.forEach((d) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`;
    }));
};

export const hasAnalyticsConsent = (): boolean => {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem("cookie-consent") === "accepted"; }
  catch { return false; }
};

export const trackPageView = (): void => {
  if (!hasAnalyticsConsent() || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_location: window.location.href,
    page_path: window.location.pathname,
    page_title: document.title,
  });
};

export const loadGoogleAnalytics = (): void => {
  if (!GA_MEASUREMENT_ID || !hasAnalyticsConsent()) return;
  (window as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  if (document.getElementById("cmo-google-analytics")) return;
  window.dataLayer = window.dataLayer || [];
  function gtag(..._args: unknown[]) {
    // gtag.js consumes Arguments objects, not rest-parameter arrays.
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA_MEASUREMENT_ID, { send_page_view: false });
  trackPageView();
  const script = document.createElement("script");
  script.id = "cmo-google-analytics";
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);
};

type EventParams = {
  event_category?: string;
  event_label?: string;
  value?: number;
  [key: string]: unknown;
};

/**
 * Track a custom event in Google Analytics 4
 */
export const trackEvent = (
  eventName: string,
  params?: EventParams
): void => {
  if (hasAnalyticsConsent() && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// Predefined conversion events
export const trackFormSubmission = (formName: string, success: boolean = true) => {
  trackEvent('form_submission', {
    event_category: 'conversion',
    event_label: formName,
    form_name: formName,
    success: success,
  });
};

export const trackCTAClick = (ctaName: string, ctaLocation: string) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: ctaName,
    cta_name: ctaName,
    cta_location: ctaLocation,
  });
};

export const trackContactClick = (method: string) => {
  trackEvent('contact_click', {
    event_category: 'engagement',
    event_label: method,
    contact_method: method,
  });
};

export const trackScrollToSection = (sectionId: string) => {
  trackEvent('scroll_to_section', {
    event_category: 'navigation',
    event_label: sectionId,
    section_id: sectionId,
  });
};
